import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLeadIn,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A literature review outline in one of three ways of organising the body — by theme,
 * by time, or by method — which are the approaches the UNC Writing Center and the USC
 * library guide both describe. The writer lists the themes, periods or methods; the
 * tool wraps each in an introduction, a body section with synthesis questions and a
 * conclusion that returns to the gap. The questions are the point: a review is
 * synthesis, not a string of summaries, and each section's checklist asks for it.
 */

export type Organization = "thematic" | "chronological" | "methodological";

export const ORGANIZATIONS: readonly Organization[] = [
  "thematic",
  "chronological",
  "methodological",
];

export interface LiteratureReviewInput {
  title: string;
  /** The question or purpose the review serves. */
  question: string;
  /** Search and selection: databases, years, inclusion criteria. */
  scope: string;
  /** The gap the review ends on. */
  gap: string;
  organization: Organization;
  /** One section per line: "Peer effects: Smith 2019; Lee 2020". */
  sections: string;
  /** Target length in words, optional. */
  words: string;
}

export const MIN_REVIEW_WORDS = 300;
export const MAX_REVIEW_WORDS = 50_000;
export const MAX_SECTIONS = 12;
/** Introduction, body and conclusion shares in percent: a planning convention. */
export const REVIEW_SHARES = { intro: 10, body: 75, conclusion: 15 } as const;
const DEFAULT_SECTIONS = 3;

export function reviewWordTotal(raw: string): number | null {
  const total = parseWholeCount(raw);
  if (total === null || total < MIN_REVIEW_WORDS || total > MAX_REVIEW_WORDS) {
    return null;
  }
  return total;
}

export interface ReviewSection {
  /** The theme, period or method as the writer named it; empty for a placeholder. */
  label: string;
  /** Sources and notes after the colon. */
  notes: string;
}

/** The writer's sections, or three placeholders while the box is empty. */
export function parseReviewSections(text: string): ReviewSection[] {
  const items = splitItems(text).slice(0, MAX_SECTIONS);
  if (items.length === 0) {
    return Array.from({ length: DEFAULT_SECTIONS }, () => ({
      label: "",
      notes: "",
    }));
  }
  return items.map((line) => {
    const parts = splitLeadIn(line);
    return parts
      ? { label: parts[0], notes: parts[1] }
      : { label: line, notes: "" };
  });
}

/** Target words for the introduction, each body section and the conclusion, in order. */
export function reviewTargets(total: number, sections: number): number[] {
  const body = REVIEW_SHARES.body / Math.max(1, sections);
  return allocateWords(
    total,
    [
      REVIEW_SHARES.intro,
      ...Array.from({ length: sections }, () => body),
      REVIEW_SHARES.conclusion,
    ],
    10
  );
}

export interface LiteratureReviewLabels {
  defaultTitle: string;
  subtitle: string;
  subtitleWords: string;
  organizations: Record<Organization, string>;
  headings: { intro: string; conclusion: string; budget: string };
  sectionHeading: string;
  sectionNames: Record<Organization, string>;
  target: string;
  tips: {
    intro: string;
    conclusion: string;
    thematic: string;
    chronological: string;
    methodological: string;
  };
  prompts: {
    background: string;
    question: string;
    scope: string;
    organizing: Record<Organization, string>;
    topicSentence: Record<Organization, string>;
    summary: string;
    gap: string;
    next: string;
  };
  notesLead: string;
  questionsHeading: string;
  questions: Record<Organization, readonly string[]>;
  closingQuestions: readonly string[];
  budgetHead: { section: string; words: string };
  budgetTotal: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function tipFor(org: Organization, labels: LiteratureReviewLabels): string {
  return labels.tips[org];
}

function guidance(
  labels: LiteratureReviewLabels,
  tip: string,
  words: number | undefined,
  fmt: Format
): DocBlock {
  const text =
    words === undefined
      ? tip
      : `${t(labels.target, { words: fmt(words) })} ${tip}`;
  return { kind: "guidance", text };
}

function bodySection(
  section: ReviewSection,
  index: number,
  input: LiteratureReviewInput,
  labels: LiteratureReviewLabels,
  words: number | undefined,
  fmt: Format
): DocSection {
  const org = input.organization;
  const name = section.label || t(labels.sectionNames[org], { n: index + 1 });
  const notes = splitLines(section.notes).join(" ");
  const blocks: DocBlock[] = [
    guidance(labels, tipFor(org, labels), words, fmt),
  ];
  if (notes) {
    blocks.push({
      kind: "paragraph",
      text: [{ text: `${labels.notesLead} `, italic: true }, { text: notes }],
    });
  }
  blocks.push(
    { kind: "prompt", text: labels.prompts.topicSentence[org] },
    { kind: "subheading", text: labels.questionsHeading },
    {
      kind: "checklist",
      items: [...labels.questions[org], ...labels.closingQuestions].map(
        (text) => ({ text })
      ),
    }
  );
  return {
    heading: t(labels.sectionHeading, { n: index + 1, name }),
    blocks,
  };
}

function introSection(
  input: LiteratureReviewInput,
  labels: LiteratureReviewLabels,
  words: number | undefined,
  fmt: Format
): DocSection {
  return {
    heading: labels.headings.intro,
    blocks: [
      guidance(labels, labels.tips.intro, words, fmt),
      { kind: "prompt", text: labels.prompts.background },
      ...paragraphsOrPrompt(input.question, labels.prompts.question),
      ...paragraphsOrPrompt(input.scope, labels.prompts.scope),
      { kind: "prompt", text: labels.prompts.organizing[input.organization] },
    ],
  };
}

function conclusionSection(
  input: LiteratureReviewInput,
  labels: LiteratureReviewLabels,
  words: number | undefined,
  fmt: Format
): DocSection {
  return {
    heading: labels.headings.conclusion,
    blocks: [
      guidance(labels, labels.tips.conclusion, words, fmt),
      { kind: "prompt", text: labels.prompts.summary },
      ...paragraphsOrPrompt(input.gap, labels.prompts.gap),
      { kind: "prompt", text: labels.prompts.next },
    ],
  };
}

function budgetSection(
  labels: LiteratureReviewLabels,
  names: string[],
  targets: number[],
  fmt: Format
): DocSection {
  const total = targets.reduce((sum, value) => sum + value, 0);
  return {
    heading: labels.headings.budget,
    blocks: [
      {
        kind: "table",
        head: [labels.budgetHead.section, labels.budgetHead.words],
        rows: [
          ...names.map((name, i) => [name, fmt(targets[i])]),
          [labels.budgetTotal, fmt(total)],
        ],
      },
    ],
  };
}

export function buildLiteratureReviewOutline(
  input: LiteratureReviewInput,
  labels: LiteratureReviewLabels,
  fmt: Format
): DocumentModel {
  const sections = parseReviewSections(input.sections);
  const total = reviewWordTotal(input.words);
  const targets = total ? reviewTargets(total, sections.length) : null;
  const word = (position: number) => targets?.[position];
  const body = sections.map((section, index) =>
    bodySection(section, index, input, labels, word(index + 1), fmt)
  );
  const names = [
    labels.headings.intro,
    ...sections.map((section, index) =>
      t(labels.sectionHeading, {
        n: index + 1,
        name:
          section.label ||
          t(labels.sectionNames[input.organization], { n: index + 1 }),
      })
    ),
    labels.headings.conclusion,
  ];
  const organization = labels.organizations[input.organization];
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle: total
      ? t(labels.subtitleWords, { organization, words: fmt(total) })
      : t(labels.subtitle, { organization }),
    sections: [
      ...(targets ? [budgetSection(labels, names, targets, fmt)] : []),
      introSection(input, labels, word(0), fmt),
      ...body,
      conclusionSection(input, labels, word(sections.length + 1), fmt),
    ],
  };
}
